import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Top-of-tree boundary. Catches render-time exceptions so the whole app
 * doesn't white-screen on a single bad component. Async errors (promises,
 * setTimeout) escape this — those should be caught at the call site or by
 * the global window error handler.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Hook into Sentry/datadog here once the DSN is configured.
    // For now, console is the audit trail.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info)
  }

  reset = (): void => this.setState({ error: null })

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset)
      return (
        <div
          role="alert"
          style={{
            padding: '64px 24px',
            maxWidth: 640,
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          <h1 className="h1" style={{ marginBottom: 12 }}>
            Something went wrong.
          </h1>
          <p className="muted" style={{ marginBottom: 24 }}>
            We logged the error. Try reloading the page; if it keeps happening, let support know.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
